/**
 * useScheduleDraftTrackConfig — ScheduleDraft 多轨显示配置状态入口。
 *
 * 本 hook 管理编辑轨、需求参考、历史快照轨、Agent 轨和外部参考轨的可见性与排序。
 * 它把 trackUtils 中的 key 规则提升为 UI 状态模型，供 canvas、Timetable 与
 * 右侧轨配置面板共享。
 *
 * 设计文档 → docs/20_workspace_ux/20_multitrack_view.md
 * 骨架关联 → lib/trackUtils.js (轨 key/条目), lib/canvas/buildScheduleDraftCanvasModel.js (画布消费), hooks/useScheduleDraftEditorUiState.js (状态组合)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { getInitialExpandedTrackIds } from "../lib/scheduleDraftCanvas.js";
import { getAgentTrackIds } from "../lib/agentTrackUtils.js";
import {
    buildExternalTrackKey,
    buildReferenceTrackItems,
    buildScheduleTrackKey,
    getReferenceDisplayTracks,
    isExternalTrackKey,
    isScheduleTrackKey,
    LIVE_REFERENCE_TRACK_TYPE,
    parseTrackKey,
    REFERENCE_TRACK_TYPE,
} from "../lib/trackUtils.js";
import useLocalStorage from "./useLocalStorage";

const isBooleanValue = (value) => typeof value === "boolean";
const isStringArray = (value) => Array.isArray(value) && value.every((item) => typeof item === "string");
const EMPTY_REFERENCE_TRACK_KEYS = [];

function uniqueKeys(keys) {
    return [...new Set(keys.filter(Boolean))];
}

function arraysEqual(left = [], right = []) {
    return left.length === right.length && left.every((item, index) => item === right[index]);
}

function getAutoVisibleReferenceTrackKeys(draft) {
    const initialTrackIds = getInitialExpandedTrackIds(draft);
    const agentTrackIds = getAgentTrackIds(draft?.tracks || []);
    const sourceReferenceTrackIds = getReferenceDisplayTracks(draft)
        .filter((track) => [REFERENCE_TRACK_TYPE, LIVE_REFERENCE_TRACK_TYPE].includes(track?.track_type))
        .map((track) => String(track.id))
        .filter(Boolean);
    return uniqueKeys([...initialTrackIds, ...agentTrackIds, ...sourceReferenceTrackIds].map(String)).map(buildScheduleTrackKey);
}

function getAvailableScheduleReferenceTrackKeys(draft) {
    return getReferenceDisplayTracks(draft).map((track) => buildScheduleTrackKey(track.id));
}

function isAvailableReferenceKey(key, availableScheduleKeySet, externalKeySet) {
    if (isScheduleTrackKey(key)) {
        return availableScheduleKeySet.has(key);
    }
    if (isExternalTrackKey(key)) {
        return externalKeySet.has(key);
    }
    return false;
}

export function useScheduleDraftTrackConfig(draft) {
    const autoVisibleReferenceTrackKeys = useMemo(() => getAutoVisibleReferenceTrackKeys(draft), [draft]);
    const availableScheduleReferenceTrackKeys = useMemo(() => getAvailableScheduleReferenceTrackKeys(draft), [draft]);
    const [showEditableTracks, setShowEditableTracks] = useLocalStorage("trackConfig.showEditableTracks", true, { scope: "draft", draftId: draft?.id, validate: isBooleanValue });
    const [showDemandReferences, setShowDemandReferences] = useLocalStorage("trackConfig.showDemandReferences", true, { scope: "draft", draftId: draft?.id, validate: isBooleanValue });
    const [referenceTrackOrderKeys, setReferenceTrackOrderKeys] = useLocalStorage("trackConfig.referenceTrackKeys", autoVisibleReferenceTrackKeys, { scope: "draft", draftId: draft?.id, validate: isStringArray });
    const [hiddenReferenceTrackKeys, setHiddenReferenceTrackKeys] = useLocalStorage("trackConfig.hiddenReferenceTrackKeys", EMPTY_REFERENCE_TRACK_KEYS, { scope: "draft", draftId: draft?.id, validate: isStringArray });
    const [externalReferenceTracks, setExternalReferenceTracks] = useState([]);

    useEffect(() => {
        setExternalReferenceTracks([]);
    }, [draft?.id]);

    const externalReferenceTrackKeys = useMemo(() => externalReferenceTracks.map((track) => track.trackKey), [externalReferenceTracks]);

    useEffect(() => {
        if (!draft?.id) return;

        const availableScheduleKeySet = new Set(availableScheduleReferenceTrackKeys);
        const externalKeySet = new Set(externalReferenceTrackKeys);
        setReferenceTrackOrderKeys((current) => {
            const next = uniqueKeys(current.filter((key) => isAvailableReferenceKey(key, availableScheduleKeySet, externalKeySet)));
            const merged = uniqueKeys([...next, ...autoVisibleReferenceTrackKeys.filter((key) => availableScheduleKeySet.has(key))]);
            return arraysEqual(current, merged) ? current : merged;
        });

        setHiddenReferenceTrackKeys((current) => {
            const next = uniqueKeys(current.filter((key) => isScheduleTrackKey(key) && availableScheduleKeySet.has(key)));
            return arraysEqual(current, next) ? current : next;
        });
    }, [availableScheduleReferenceTrackKeys, autoVisibleReferenceTrackKeys, draft?.id, externalReferenceTrackKeys, setHiddenReferenceTrackKeys, setReferenceTrackOrderKeys]);

    const referenceTrackKeys = useMemo(() => {
        const availableScheduleKeySet = new Set(availableScheduleReferenceTrackKeys);
        const externalKeySet = new Set(externalReferenceTrackKeys);
        const hiddenKeySet = new Set(hiddenReferenceTrackKeys);
        const orderedKeys = uniqueKeys(referenceTrackOrderKeys.filter((key) => isAvailableReferenceKey(key, availableScheduleKeySet, externalKeySet)));
        const mergedKeys = uniqueKeys([...orderedKeys, ...autoVisibleReferenceTrackKeys.filter((key) => availableScheduleKeySet.has(key))]);
        return mergedKeys.filter((key) => !isScheduleTrackKey(key) || !hiddenKeySet.has(key));
    }, [availableScheduleReferenceTrackKeys, autoVisibleReferenceTrackKeys, externalReferenceTrackKeys, hiddenReferenceTrackKeys, referenceTrackOrderKeys]);

    const visibleReferenceTrackIds = useMemo(
        () => referenceTrackKeys.filter(isScheduleTrackKey).map((key) => parseTrackKey(key).id),
        [referenceTrackKeys],
    );

    const visibleExternalReferenceTracks = useMemo(() => {
        const tracksByKey = new Map(externalReferenceTracks.map((track) => [track.trackKey, track]));
        return referenceTrackKeys.filter(isExternalTrackKey).map((key) => tracksByKey.get(key)).filter(Boolean);
    }, [externalReferenceTracks, referenceTrackKeys]);

    const referenceTrackItems = useMemo(
        () => buildReferenceTrackItems(referenceTrackKeys, draft?.tracks, externalReferenceTracks),
        [draft?.tracks, externalReferenceTracks, referenceTrackKeys],
    );

    const toggleReferenceTrack = useCallback(
        (trackId) => {
            const trackKey = buildScheduleTrackKey(trackId);
            setHiddenReferenceTrackKeys((current) => {
                if (referenceTrackKeys.includes(trackKey)) {
                    return current.includes(trackKey) ? current : [...current, trackKey];
                }
                return current.filter((item) => item !== trackKey);
            });
            setReferenceTrackOrderKeys((current) => (current.includes(trackKey) ? current : [...current, trackKey]));
        },
        [referenceTrackKeys, setHiddenReferenceTrackKeys, setReferenceTrackOrderKeys],
    );

    const addReferenceTrack = useCallback(
        (trackId) => {
            const trackKey = buildScheduleTrackKey(trackId);
            setHiddenReferenceTrackKeys((current) => current.filter((item) => item !== trackKey));
            setReferenceTrackOrderKeys((current) => (current.includes(trackKey) ? current : [...current, trackKey]));
        },
        [setHiddenReferenceTrackKeys, setReferenceTrackOrderKeys],
    );

    const addExternalReferenceTrack = useCallback(
        (referenceSource) => {
            const trackKey = buildExternalTrackKey(referenceSource);
            const nextTrack = { ...referenceSource, trackKey };
            setExternalReferenceTracks((current) => [...current.filter((item) => item.trackKey !== trackKey), nextTrack]);
            setReferenceTrackOrderKeys((current) => (current.includes(trackKey) ? current : [...current, trackKey]));
        },
        [setReferenceTrackOrderKeys],
    );

    const removeReferenceTrack = useCallback(
        (trackKey) => {
            if (isScheduleTrackKey(trackKey)) {
                setHiddenReferenceTrackKeys((current) => (current.includes(trackKey) ? current : [...current, trackKey]));
                return;
            }
            if (isExternalTrackKey(trackKey)) {
                setReferenceTrackOrderKeys((current) => current.filter((item) => item !== trackKey));
                setExternalReferenceTracks((current) => current.filter((item) => item.trackKey !== trackKey));
            }
        },
        [setHiddenReferenceTrackKeys, setReferenceTrackOrderKeys],
    );

    const moveReferenceTrack = useCallback(
        (trackKey, direction) => {
            const currentIndex = referenceTrackKeys.indexOf(trackKey);
            if (currentIndex === -1) return;

            const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
            if (targetIndex < 0 || targetIndex >= referenceTrackKeys.length) return;

            const next = [...referenceTrackKeys];
            [next[currentIndex], next[targetIndex]] = [next[targetIndex], next[currentIndex]];
            setReferenceTrackOrderKeys(next);
        },
        [referenceTrackKeys, setReferenceTrackOrderKeys],
    );

    const moveReferenceScheduleTrack = useCallback((trackId, direction) => moveReferenceTrack(buildScheduleTrackKey(trackId), direction), [moveReferenceTrack]);

    return {
        showEditableTracks,
        setShowEditableTracks,
        showDemandReferences,
        setShowDemandReferences,
        referenceTrackKeys,
        referenceTrackItems,
        externalReferenceTracks: visibleExternalReferenceTracks,
        visibleReferenceTrackIds,
        toggleReferenceTrack,
        addReferenceTrack,
        addExternalReferenceTrack,
        removeReferenceTrack,
        moveReferenceTrack,
        moveReferenceScheduleTrack,
    };
}

export default useScheduleDraftTrackConfig;
